<template>
  <div id="app">
    <VApp
      id="huntica-theme"
      :class="{'interview-iframe': $route.meta.iframe}"
    >
      <RouterView v-if="isReady" />
      <div v-else class="fouc-overlay">
        &#8203;
      </div>
      <AppCookieLaw v-if="isReady && !$route.meta.unsupportedCookies" />
    </VApp>
  </div>
</template>

<script>
import { onMounted, ref } from '@vue/composition-api';
import { mapGetters } from 'vuex';
import { cabinetUrl } from '@/functions';
import { HOME_DOMAIN } from '@/helpers/constants';
import Sentry from '@/sentry';
import LogRocket from '@/logrocket';
import Logo from '@/assets/img/huntica-logo.png';
import useLocale from '@/compositions/useLocale';
import useUtm from '@/compositions/useUtm';
import Datadog from './datadog';

export default {
  name: 'App',
  components: { AppCookieLaw: () => import('@/components/AppCookieLaw.vue') },
  metaInfo() {
    return {
      title: this.$t('slogan'),
      meta: [
        { property: 'og:title', content: 'Huntica' },
        { property: 'og:url', content: `https://${this.HOME_DOMAIN}/` },
        { property: 'og:description', content: this.$t('description') },
        { property: 'og:image', content: Logo },
        { name: 'twitter:card', content: 'summary' },
      ],
      htmlAttrs: {
        lang: this.$i18n.locale,
      },
    };
  },
  computed: {
    ...mapGetters('candidate', ['candidate']),
    companyId() {
      return this.$route.params.companyId || null;
    },
    HOME_DOMAIN() {
      return HOME_DOMAIN;
    },
  },
  created() {
    this.setCookiesData();
  },
  methods: {
    setCookiesData() {
      const { referralCode } = this.$route.query;

      if (referralCode) {
        document.cookie = `referralCode=${referralCode}`;
      }
    },
  },
  watch: {
    candidate(newValue) {
      Sentry.setUser({
        id: newValue ? `candidate-${newValue.id}` : null,
        email: newValue ? newValue.email : null,
        username: newValue ? `${newValue.firstName} ${newValue.lastName}` : null,
      });

      if (newValue === null) {
        LogRocket.identify(null);
      } else {
        LogRocket.identify(`candidate-${newValue.id}`, {
          name: `${newValue.firstName} ${newValue.lastName}`,
          email: newValue.email,
          authType: 'candidate',
          link: cabinetUrl(`vacancy/${newValue.vacancyId}/candidates/${newValue.id}`),
        });
      }
    },
  },
  setup(_, { root }) {
    const { changeLocale, detectLocale, saveLocaleIfEmptyStorageValue } = useLocale();
    const { extractUtm } = useUtm();
    const isReady = ref(false);

    onMounted(() => {
      Datadog.init();
      console.log('onMounted in app', process.server);
      const locale = detectLocale();
      changeLocale(locale);
      saveLocaleIfEmptyStorageValue(locale);
      isReady.value = true;
      root.$router.onReady(() => extractUtm(root.$route.query));
    });

    return {
      isReady,
    };
  },
};
</script>

<style lang="scss">
  @import "assets/css/style";
  @import '../node_modules/@huntica/huntica-interview-module/src/sass/video-player.scss';
</style>

<style lang="scss" scoped>
  .fouc-overlay {
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    z-index: 1000000;
  }

  .interview-iframe {
    background: none !important;
  }
</style>

<i18n locale="en" lang="yaml">
  slogan: "Huntica - the first automated interview service with a real person"
  description: >-
    Our online interview service reduces time spent on routine tasks
    and helps focus only on those that cannot be automated. As a result,
    recruitment time is reduced, human errors are minimized, and the cost of hiring is lowered.
</i18n>

<i18n locale="ru" lang="yaml">
  slogan: "Huntica — сервис виртуальных собеседований с человеческим лицом"
  description: >-
    Удобный интерфейс позволяет быстро создавать вакансии, на которые кандидаты будут записывать
    видео-отклики. Приглашайте кандидатов из личного кабинета, с помощью расширения для Chrome,
    или разместите открытую вакансию, на которую сможет откликнуться любой желающий.
</i18n>
